// Mechanic performance: jobs done, book hours, what their work brought in on invoices, and how it compares with their pay.
import { useState } from 'react';
import { useApi, dollars, hours, fmtDate, today, navigate, type Row } from '../lib';
import { Panel, Table, Loading, ErrorBox, Stat, Badge, Link, Icon, Empty } from '../ui';

function useRange() {
  const t = today();
  const y = t.slice(0, 4), m = Number(t.slice(5, 7));
  const lastStart = m === 1 ? `${Number(y) - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
  const lastEnd = new Date(Date.parse(t.slice(0, 8) + '01T12:00:00Z') - 864e5).toISOString().slice(0, 10);
  const back = (days: number) => new Date(Date.parse(t + 'T12:00:00Z') - days * 864e5).toISOString().slice(0, 10);
  const presets: [string, string, string][] = [['This month', t.slice(0, 8) + '01', t], ['Last month', lastStart, lastEnd], ['This year', `${y}-01-01`, t],
    ['Last 90 days', back(90), t], ['Last 12 months', back(365), t], ['Last 5 years', back(1826), t]];
  const [r, setR] = useState<[string, string]>([presets[0][1], presets[0][2]]);
  return { from: r[0], to: r[1], presets, set: (a: string, b: string) => setR([a, b]) };
}

const comebacks = (x: Row) => x.comebacks ? <span className="danger"><b>{x.comebacks}</b>{x.jobs_done ? ` · ${Math.round((x.comebacks / x.jobs_done) * 100)}%` : ''} · {dollars(x.comeback_cost)}</span> : <span className="muted">0</span>;
const dur = (sec: number) => { const m = Math.round(sec / 60); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };
const eff = (x: Row) => x.efficiency === null || x.efficiency === undefined ? '—' : `${x.efficiency}%`;
const covers = (x: Row) => x.covers === null || x.covers === undefined ? <span className="muted">no pay set</span>
  : <span className={x.over_pay >= 0 ? 'good-text' : 'danger'}>{x.covers.toFixed(1)}× pay</span>;

function RangeBar({ r }: { r: ReturnType<typeof useRange> }) {
  return (
    <div className="toolbar">
      {r.presets.map(([l, a, b]) => <button key={l} className={'chip' + (r.from === a && r.to === b ? ' on' : '')} onClick={() => r.set(a, b)}>{l}</button>)}
      <label className="inline">From <input type="date" value={r.from} onChange={e => r.set(e.target.value, r.to)} /></label>
      <label className="inline">To <input type="date" value={r.to} onChange={e => r.set(r.from, e.target.value)} /></label>
    </div>
  );
}

export function Performance() {
  const r = useRange();
  const { data, error, reload } = useApi<Row>(`/api/performance?from=${r.from}&to=${r.to}`, [r.from, r.to]);
  return (
    <>
      <div className="page-head"><div><h1>Team performance</h1><p>Who did which jobs, what their work brought in on invoices, and whether it covers their pay.</p></div></div>
      <RangeBar r={r} />
      <ErrorBox error={error} onRetry={reload} />
      {!data ? <Loading /> : (
        <Panel>
          <Table heads={['Mechanic', 'Jobs done', 'Trucks', 'Book hrs', 'Clocked hrs', 'Efficiency', 'Labor billed', 'Parts profit', 'Pay', 'Covers', 'Comebacks', 'Open now']} empty="No mechanics yet."
            rows={(data.team as Row[]).map(x => [
              <b>{x.name}</b>, x.jobs_done, x.trucks, hours(x.book_hours), x.clocked_hours ? hours(x.clocked_hours) : '—', eff(x),
              <b>{dollars(x.labor_billed)}</b>, dollars(x.parts_profit), x.pay ? dollars(x.pay) : '—', covers(x), comebacks(x), x.open_jobs || '—',
            ])}
            onRow={i => navigate(`/performance/${data.team[i].user_id}`)} />
          <p className="form-note pad">
            <b>Book hrs</b> = hours billed on invoices (from the labor guide), however long it took. <b>Efficiency</b> = book hours ÷ clocked hours (over 100% = faster than book).
            <b> Covers</b> = (labor billed + parts profit on their jobs) ÷ their pay for the period. Counts invoices issued in the period.
            <b> Comebacks</b> = their jobs that had to be done again (marked "mechanic's mistake"), with what the redo cost: parts again at cost + the labor that couldn't be billed.
          </p>
        </Panel>
      )}
    </>
  );
}

export function PersonPerformance({ id }: { id: string }) {
  const r = useRange();
  const { data, error, reload } = useApi<Row>(`/api/performance/${id}?from=${r.from}&to=${r.to}`, [id, r.from, r.to]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const s = data.summary as Row | null;
  return (
    <>
      <div className="page-head"><div>
        <Link to="/performance" className="back"><Icon name="back" size={16} />Team performance</Link>
        <h1>{s?.name || 'Mechanic'}</h1><p>{fmtDate(data.from)} – {fmtDate(data.to)}</p></div></div>
      <RangeBar r={r} />
      {s && <div className="stats">
        <Stat label="Labor billed" value={dollars(s.labor_billed)} hint={`${hours(s.book_hours)} book hrs · ${s.trucks} trucks`} />
        <Stat label="Brought in" value={dollars(s.brought_in)} hint={`Labor + ${dollars(s.parts_profit)} parts profit`} />
        <Stat label="Pay" value={s.pay ? dollars(s.pay) : '—'} hint={s.pay ? (s.over_pay >= 0 ? `${dollars(s.over_pay)} more than pay` : `${dollars(-s.over_pay)} short of pay`) : 'No pay set'} tone={s.pay && s.over_pay < 0 ? 'warn' : ''} />
        <Stat label="Efficiency" value={eff(s)} hint={s.clocked_hours ? `${hours(s.book_hours)} book ÷ ${hours(s.clocked_hours)} clocked` : 'No clock hours'} />
        <Stat label="Comebacks" value={String(s.comebacks || 0)} hint={s.comebacks ? `Cost the shop ${dollars(s.comeback_cost)}${s.jobs_done ? ` · ${Math.round((s.comebacks / s.jobs_done) * 100)}% of jobs done` : ''}` : 'No redo in this period'} tone={s.comebacks ? 'warn' : ''} />
      </div>}
      <div className="grid-2 wide-left">
        <Panel title="Jobs invoiced">
          <Table heads={['Date', 'WO', 'Truck', 'Job', 'Book hrs', 'Took', 'Billed']} empty="No invoiced work in this period."
            rows={(data.jobs as Row[]).map(j => [fmtDate(j.issued_on), j.number, <><b>#{j.unit || '—'}</b><small className="muted">{j.customer}</small></>,
              <>{j.job}{j.came_back > 0 && <> <Badge tone="red">↩ Came back</Badge></>}</>, j.book ? hours(j.book) : '—',
              j.worked_sec > 0 ? <span className={j.book && j.worked_sec / 36 > j.book * 1.25 ? 'warn-text' : ''}>{dur(j.worked_sec)}</span> : <span className="muted">—</span>, <b>{dollars(j.billed)}</b>])}
            onRow={i => navigate('/work-orders/' + data.jobs[i].wo_id)} />
          <p className="form-note pad"><b>Took</b> = their time on the phone (Start → End). Orange = over the book hours by more than 25%.</p>
        </Panel>
        <Panel title={`Comebacks — ${(data.comebacks as Row[]).length}${(data.comebacks as Row[]).length ? ' · cost ' + dollars((data.comebacks as Row[]).reduce((a, x) => a + x.cost, 0)) : ''}`}>
          {(data.comebacks as Row[]).length ? <ul className="plain-list cb-list">{(data.comebacks as Row[]).map(x => (
            <li key={x.id}><Link to={'/work-orders/' + x.wo_id}><b>{x.title}</b></Link> <small className="muted">#{x.unit || '—'} · {x.customer || ''} · {fmtDate(String(x.comeback_at).slice(0, 10))}</small>
              <small className="cb-first">First time: {x.first_title} · {x.first_day ? fmtDate(x.first_day) : ''} · {x.first_wo}</small>
              <small>Redo: {(x.parts as Row[]).map(p => `${p.description}${p.qty > 1 ? ' ×' + p.qty : ''}`).join(', ') || 'labor only'} — <b className="danger">{dollars(x.cost)}</b> ({dollars(x.parts_cost)} parts + {hours(x.hours)} h labor)</small></li>))}</ul>
            : <Empty>No comebacks in this period. 👍</Empty>}
        </Panel>
        <Panel title="On their plate now">
          {(data.open as Row[]).length ? <ul className="plain-list">{(data.open as Row[]).map(j => (
            <li key={j.id}><Link to={'/work-orders/' + j.wo_id}><b>{j.title}</b></Link> <Badge tone={j.status === 'doing' ? 'amber' : 'gray'}>{j.status === 'doing' ? 'Working' : 'To do'}</Badge>
              <small className="muted"> #{j.unit || '—'} · {j.customer || ''} · {j.number}</small></li>))}</ul> : <Empty>Nothing assigned right now.</Empty>}
        </Panel>
      </div>
    </>
  );
}

/** Dashboard card: each mechanic this month — billed vs pay. */
export function TeamCard() {
  const { data } = useApi<Row>('/api/performance');
  if (!data || !(data.team as Row[]).length) return null;
  const team = (data.team as Row[]).filter(x => x.role === 'mechanic' || x.labor_billed > 0);
  return (
    <Panel title="Team this month" action={<Link to="/performance">Details →</Link>}>
      <Table heads={['Mechanic', 'Jobs', 'Labor billed', 'Pay', 'Covers']} empty="No mechanics yet."
        rows={team.map(x => [<b>{x.name}</b>, x.jobs_done || x.trucks, dollars(x.labor_billed), x.pay ? dollars(x.pay) : '—', covers(x)])}
        onRow={i => navigate(`/performance/${team[i].user_id}`)} />
    </Panel>
  );
}
